"use client";

import { useState } from "react";
import { HelpCircle, RefreshCw } from "lucide-react";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Textarea } from "@super-resume-new/ui/components/textarea";

export type GapAnswer = { keyword: string; question: string; answer: string };

type Question = { keyword: string; question: string; suggested_fix?: string };

export function GapQuestionnaire({
  questions,
  onRerun,
  busy,
}: {
  questions: Question[];
  onRerun: (answers: GapAnswer[]) => void;
  busy: boolean;
}) {
  const [answers, setAnswers] = useState<Record<number, string>>({});

  if (questions.length === 0) return null;

  const filled = questions
    .map((q, i) => ({
      keyword: q.keyword,
      question: q.question,
      answer: (answers[i] ?? "").trim(),
    }))
    .filter((a) => a.answer.length > 0);

  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <HelpCircle size={16} className="text-brand shrink-0" aria-hidden="true" />
        Answer {questions.length} {questions.length === 1 ? "question" : "questions"} to strengthen
        this application
      </h3>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Each of these could not be settled from your resume alone — a missing keyword, or a
        requirement this posting asks about. Answer any of them and the rewrite uses the facts you
        give.
      </p>

      <div className="flex flex-col gap-2">
        {questions.map((q, i) => (
          <div
            key={`${q.keyword}-${i}`}
            className="rounded-xl border border-border bg-card p-3 shadow-sm flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <Badge variant="info" className="text-[11px] py-0 px-2">
                {q.keyword}
              </Badge>
            </div>
            <p className="text-xs font-medium text-foreground leading-relaxed">{q.question}</p>
            <Textarea
              size="sm"
              autoResize
              aria-label={`Your answer about ${q.keyword}`}
              placeholder="Your answer — leave blank to skip"
              value={answers[i] ?? ""}
              disabled={busy}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <p className="text-[11px] text-muted-foreground leading-snug">
          Regenerating runs a new rewrite and costs 1 credit.
        </p>
        <Button
          onClick={() => onRerun(filled)}
          disabled={busy || filled.length === 0}
          loading={busy}
        >
          {!busy && <RefreshCw aria-hidden="true" />}
          {busy ? "Regenerating…" : "Regenerate with my answers"}
        </Button>
      </div>
    </section>
  );
}
